import { getAllChatModels } from './chat-models-source';

/**
 * FE#430 stopgap — a brand-new incoming chat must surface even while a Blueticks
 * label/tab filter is active.
 *
 * `updateShouldAppearChatsByList` applies a tab by reassigning `Store.Chat._models`
 * to only the chats matching the active label; the full set still lives in
 * `Store.Chat._index`. A message arriving for a chat that is not in the active label
 * leaves that chat out of `_models`, so the new conversation never appears in WhatsApp
 * Web until a reload rebuilds the list from the index. This surfaces the incoming chat
 * back into the rendered list the moment it arrives, on ANY active tab.
 *
 * The long-term fix is to stop mutating `Store.Chat._models` and filter at the view
 * layer (tracked as the FE#430 follow-up); this is the localized, low-risk stopgap.
 */

/**
 * Pure decision + transform. Given the currently-rendered `models`, the full index
 * size, and the incoming chat, return the models the rendered list should show.
 *
 * Surfaces the incoming chat at the TOP (new activity) only when a filter is actually
 * active (rendered `_models` is a strict subset of the index) AND the chat is both
 * missing from the rendered list and present in the index. Otherwise it returns the
 * same array reference unchanged, so nothing is rebuilt or reordered needlessly.
 */
export function computeModelsWithIncomingChat<T>(
  models: T[],
  fullIndexCount: number,
  incomingChatId: string,
  incomingChatModel: T | undefined,
  idOf: (m: T) => string,
): { changed: boolean; models: T[] } {
  // A filter is active only when fewer chats are rendered than the index holds.
  const filterActive = models.length < fullIndexCount;
  if (!filterActive) return { changed: false, models };
  if (!incomingChatModel) return { changed: false, models };
  if (models.some((m) => idOf(m) === incomingChatId)) return { changed: false, models };
  return { changed: true, models: [incomingChatModel, ...models] };
}

/** Minimal shape of the WhatsApp Web chat store this module touches. */
interface WaId {
  _serialized?: string;
  toString?: () => string;
}
interface WaChatModel {
  id?: WaId;
  t?: number;
}
interface WaChatCollection {
  _models: WaChatModel[];
  _index?: Record<string, WaChatModel>;
  trigger?: (event: string) => void;
}
interface WaStore {
  Chat?: WaChatCollection;
}

/** Serialized id for a WhatsApp Chat model (matches the `_index` keys / filter logic). */
export function chatIdOf(model: WaChatModel | null | undefined): string {
  const id = model?.id;
  return (id?._serialized ?? id?.toString?.()) || '';
}

/**
 * Store-coupled wrapper: ensure the incoming chat is visible in the filtered chat list.
 *
 * No-op when no label/tab filter is active (the full list is already shown). When a
 * filter is active and the incoming chat was dropped from `Store.Chat._models`, prepend
 * it and force WhatsApp to re-render (see `forceChatListRerender`).
 *
 * Best-effort: never throws into the incoming-message path.
 */
export function ensureChatVisibleInFilteredList(incomingChatId: string): boolean {
  try {
    if (!incomingChatId) return false;
    const Store: WaStore | undefined = window.getStore?.();
    const Chat = Store?.Chat;
    if (!Chat || !Array.isArray(Chat._models)) return false;

    // Authoritative full set (built once): drives both the filter-active check and the
    // fallback lookup of the incoming chat's model.
    const allModels: WaChatModel[] = getAllChatModels(Store);
    const incomingModel =
      (Chat._index && Chat._index[incomingChatId]) ||
      allModels.find((m) => chatIdOf(m) === incomingChatId);

    const { changed, models } = computeModelsWithIncomingChat(
      Chat._models,
      allModels.length,
      incomingChatId,
      incomingModel,
      chatIdOf,
    );
    if (!changed) return false;

    Chat._models = models;
    forceChatListRerender(Chat);
    return true;
  } catch {
    // A render-surfacing best-effort must never break message handling.
    return false;
  }
}

/**
 * Mirror the render-forcing `updateShouldAppearChatsByList` uses after it mutates
 * `Store.Chat._models`. WhatsApp Web does NOT reliably repaint the chat list on a bare
 * collection event alone — the empty-then-restore reference swap (step 3) is what forces
 * it (verified on the rig: an appended chat only became visible with this full sequence).
 *
 * Each collection event is guarded individually: WA's own `change`/`sort` handlers can
 * throw on a transient/stub model that happens to be in the collection, and one throwing
 * event must not prevent the reference-swap repaint from running.
 */
function forceChatListRerender(Chat: WaChatCollection): void {
  const fire = (ev: string) => {
    try {
      if (typeof Chat.trigger === 'function') Chat.trigger(ev);
    } catch {
      /* best-effort render nudge */
    }
  };
  // 1) collection events on the populated array
  fire('change');
  fire('sort');
  fire('reset');
  fire('add');
  fire('remove');
  // 2) nudge the first row's sort key so the list treats the collection as changed
  try {
    const first = Chat._models[0];
    if (first && first.t !== undefined) first.t = first.t + 1;
  } catch {
    /* ignore */
  }
  // 3) swap the collection reference (empty -> restore) to force the repaint
  try {
    const current = Chat._models;
    Chat._models = [];
    setTimeout(() => {
      Chat._models = current;
      fire('reset');
    }, 10);
  } catch {
    /* ignore */
  }
}
